import {GetAnalysisPipelineStep, GetAnalysisStepData} from 'analysis-tool/AnalysisTypes';
import {
  LINEAGE_TERMINAL_STEP_TYPES,
  getTableLineageAncestorInformation,
} from 'analysis-tool/data/DatasetLineageUtils';

import getLegacyStepFromAnalysis from '../analysis-tool/data/getStepFromAnalysis';
import {PipelineGraphQL, PipelineStep} from './PipelineTypes';

/**
 * Given an pipeline and a step, return all children steps of that step
 */
export function getChildSteps(
  pipeline: PipelineGraphQL,
  step: GetAnalysisPipelineStep
): Array<GetAnalysisPipelineStep> {
  return step.definition.outgoingEdges.map((edge) => getLegacyStepFromAnalysis(pipeline, edge.child.step.id));
}

/**
 * Gather all the ancestors in a single dataset lineage (inclusive).
 *
 * @deprecated This util makes dangerous assumptions about the shape of the DAG
 */
export function getTableLineageAncestorSteps(
  pipeline: PipelineGraphQL,
  step: GetAnalysisPipelineStep
): Array<GetAnalysisPipelineStep> {
  return getTableLineageAncestorInformation(pipeline, {step}).nodes.map((lineageNode) => lineageNode.step);
}

function stepDatasContainDataFrame(stepDatas: Array<GetAnalysisStepData>, dataFrameId: string): boolean {
  return stepDatas.some(({dataFrames}) =>
    dataFrames.some(({id: otherDataFrameId}) => otherDataFrameId === dataFrameId)
  );
}

/**
 * Given an pipeline, a step, and a step data, return all children steps that have an edge to that step data
 */
export function getChildStepsForDataFrame(
  pipeline: PipelineGraphQL,
  step: GetAnalysisPipelineStep,
  dataFrameId: string
): Array<GetAnalysisPipelineStep> {
  if (!stepDatasContainDataFrame(step.outputStepDatas, dataFrameId)) {
    return [];
  }

  return getChildSteps(pipeline, step).filter((childStep) =>
    stepDatasContainDataFrame(childStep.inputStepDatas, dataFrameId)
  );
}

/**
 * Given an pipeline and a step, return all parent steps of that step
 */
export function getParentSteps(
  pipeline: PipelineGraphQL,
  step: GetAnalysisPipelineStep
): Array<GetAnalysisPipelineStep> {
  return step.definition.incomingEdges.map((edge) =>
    getLegacyStepFromAnalysis(pipeline, edge.parent.step.id)
  );
}

/**
 * Given an pipeline and a step, return the origin step of the lineage.
 */
export function getOriginOrTerminalStep(
  pipeline: PipelineGraphQL,
  step: GetAnalysisPipelineStep
): GetAnalysisPipelineStep {
  return step.definition.incomingEdges.length === 0 ||
    LINEAGE_TERMINAL_STEP_TYPES.includes(step.definition.stepType)
    ? step
    : getOriginOrTerminalStep(pipeline, getParentSteps(pipeline, step)[0]);
}

interface TraversePipelineOptions {
  /**
   * The steps from which to start the traversal.
   *
   * If not provided, the traversal starts from the pipeline's source steps (i.e. all steps with no incoming
   * edges).
   */
  startingSteps?: Iterable<PipelineStep>;
  /**
   * A function to check if a step matches a condition; only steps that match will be returned.
   *
   * If not provided, all traversed steps will be returned, including `startingSteps`.
   */
  checkMatchFn?: (step: PipelineStep) => boolean;
  /**
   * Whether to stop traversing at the first match.
   *
   * If false or not provided, all matching steps are returned.
   */
  stopAtMatch?: boolean;
  /**
   * Whether to traverse upwards towards ancestors, instead of traversing downwards towards descendants.
   */
  traverseAncestors?: boolean;
}

/**
 * Search for pipeline steps using a depth-first search traversal.
 *
 * `startingSteps` are included in the response, if they match `checkMatchFn`.
 *
 * @param pipeline The pipeline the steps belong to, used to resolve edges to their connected steps.
 * @param options {TraversePipelineOptions} Options defining the traversal.
 */
export function traversePipeline(
  pipeline: Pick<PipelineGraphQL, 'id' | 'steps'>,
  {startingSteps, checkMatchFn, stopAtMatch = false, traverseAncestors = false}: TraversePipelineOptions = {}
): Array<PipelineStep> {
  const stepsById = new Map(pipeline.steps.map((step) => [step.id, step]));

  const getAdjacentSteps = (step: PipelineStep): Array<PipelineStep> =>
    traverseAncestors
      ? step.incomingEdges.map((edge) => stepsById.get(edge.fromStep.id)!)
      : step.outgoingEdges.map((edge) => stepsById.get(edge.toStep.id)!);

  const matches: Array<PipelineStep> = [];
  const stack =
    startingSteps != null
      ? [...startingSteps]
      : pipeline.steps.filter((step) => step.incomingEdges.length === 0);
  const visited = new Set<string>();

  while (stack.length > 0) {
    const current = stack.pop();
    if (current == null || visited.has(current.id)) {
      continue;
    }
    if (checkMatchFn == null || checkMatchFn(current)) {
      matches.push(current);
      if (stopAtMatch) {
        break;
      }
    }
    stack.push(...getAdjacentSteps(current));
    visited.add(current.id);
  }

  return matches;
}
